// ─── Hero: Dark Cinematic ─────────────────────────────────────
// Full-viewport dark hero with deep overlay, sparkle badge,
// staggered title text, and CTA button. Luxe dark-theme feel.

import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { AnimatedSection, StaggerContainer } from '../../../lib/animations/AnimatedSection';

export default function HeroDarkCinematic({ section, data, theme, settings, rsvpRef }) {
  const { title, subtitle, dateText, backgroundImage, introLabel, ctaText, ctaTarget } = data;
  const anim = section.animation || {};

  const scrollToTarget = () => {
    if (ctaTarget && ctaTarget !== 'rsvp') {
      const el = document.querySelector(`[id^="${ctaTarget}-"]`) || document.querySelector(`[data-section-type="${ctaTarget}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    rsvpRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Background with dark overlay */}
      <AnimatedSection preset={anim.image || 'zoom-soft'} className="absolute inset-0">
        {backgroundImage ? (
          <div
            className="h-full w-full bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.46), rgba(0,0,0,0.58)), url(${backgroundImage})`,
            }}
          />
        ) : (
          <div className="h-full w-full" style={{ background: theme.surface }} />
        )}
      </AnimatedSection>

      {/* Bottom gradient fade to bg */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
        style={{ background: `linear-gradient(to top, ${theme.bg}, transparent)` }}
      />

      {/* Content — bottom-aligned */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-end px-6 py-14 sm:px-8 sm:py-20">
        <div className="max-w-4xl">
          {/* Badge */}
          <AnimatedSection preset="fade-in">
            <div
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 mb-6"
              style={{ borderColor: `${theme.accent}55`, backgroundColor: `${theme.bg}40` }}
            >
              <Sparkles className="h-4 w-4" style={{ color: theme.accent }} />
              <span
                className="text-[11px] uppercase tracking-[0.18em]"
                style={{ color: 'rgba(255,255,255,0.90)', fontFamily: theme.eyebrowFont }}
              >
                {introLabel || 'Celebration'}
              </span>
            </div>
          </AnimatedSection>

          {/* Title */}
          <StaggerContainer preset={anim.title || 'stagger-text'}>
            <h1
              className="text-5xl leading-[0.92] sm:text-6xl md:text-[6.5rem]"
              style={{ color: '#FFFFFF', fontFamily: theme.headingFont }}
            >
              {title || 'Our Wedding'}
            </h1>
          </StaggerContainer>

          {/* Subtitle */}
          <AnimatedSection preset={anim.subtitle || 'fade-in'} delay={0.3}>
            <p className="mt-6 max-w-2xl text-base leading-7 sm:text-lg" style={{ color: 'rgba(255,255,255,0.85)' }}>
              {subtitle}
            </p>
          </AnimatedSection>

          {/* Date + CTA */}
          <AnimatedSection preset="fade-in" delay={0.5}>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.78)' }}>
              <span style={{ fontFamily: theme.eyebrowFont }}>{dateText}</span>
              {ctaText && (
              <button
                onClick={scrollToTarget}
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 normal-case tracking-normal transition hover:opacity-90"
                style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
                data-testid="hero-rsvp-cta"
              >
                {ctaText}
                <ArrowRight className="h-4 w-4" />
              </button>
              )}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
